/**
 * CR311: Simplified Preview Controls
 * CR500: Added forced renderer state from rollout config
 * 
 * Default-visible controls: Face / Breakpoint / Scale
 * Advanced controls accessible behind toggle: custom dimensions, overlays, zoom
 * 
 * When V2 rollout is ON (100%), the Renderer toggle is disabled and locked to V2.
 */

import { useCallback } from "react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import { Separator } from "@/components/ui/separator";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import {
  Monitor,
  Smartphone,
  Tablet,
  Maximize2,
  Minimize2,
  Square,
  Layers,
  ChevronDown,
  Settings2,
  ZoomIn,
  ZoomOut,
  Grid3X3,
  Ruler,
  Box,
  EyeOff,
  RotateCcw,
  ArrowUpDown,
} from "lucide-react";
import type {
  PreviewViewportWidth,
  PreviewScaleMode,
  PreviewFaceView,
  PreviewDisplayMode,
  PreviewHeightMode,
  PreviewDebugOverlays,
  PreviewControlsState,
} from "@/lib/deals/cardFaces";

interface SimplePreviewControlsProps {
  controls: PreviewControlsState;
  onChange: (update: Partial<PreviewControlsState>) => void;
  onReset?: () => void;
  className?: string;
}

// Breakpoint presets
const BREAKPOINT_PRESETS: { value: PreviewViewportWidth; label: string; icon: React.ComponentType<{ className?: string }> }[] = [
  { value: 200, label: "200", icon: Smartphone },
  { value: 400, label: "400", icon: Tablet },
  { value: 600, label: "600", icon: Monitor },
];

export function SimplePreviewControls({
  controls,
  onChange,
  onReset,
  className,
}: SimplePreviewControlsProps) {
  
  
  // Handlers
  const handleFaceViewChange = useCallback((value: string) => {
    if (value) onChange({ faceView: value as PreviewFaceView });
  }, [onChange]);
  
  const handleBreakpointChange = useCallback((value: string) => {
    if (!value) return;
    if (value === "custom") {
      onChange({ viewportWidth: "custom" });
    } else {
      onChange({ viewportWidth: parseInt(value) as PreviewViewportWidth });
    }
  }, [onChange]);
  
  const handleDisplayModeChange = useCallback((value: string) => {
    if (value) onChange({ displayMode: value as PreviewDisplayMode });
  }, [onChange]);
  
  const handleHeightModeChange = useCallback((value: string) => {
    if (value) onChange({ heightMode: value as PreviewHeightMode });
  }, [onChange]);
  
  const handleScaleModeChange = useCallback((value: string) => {
    if (value) onChange({ scaleMode: value as PreviewScaleMode });
  }, [onChange]);
  
  const handleCustomWidthChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value) || 400;
    onChange({ customWidth: Math.max(200, Math.min(1000, value)) });
  }, [onChange]);
  
  const handleCustomHeightChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value) || 520;
    onChange({ customHeight: Math.max(200, Math.min(1200, value)) });
  }, [onChange]);
  
  const handleCustomScaleChange = useCallback((values: number[]) => {
    onChange({ customScale: values[0] });
  }, [onChange]);
  
  const handleOverlayToggle = useCallback((key: keyof PreviewDebugOverlays) => {
    onChange({
      overlays: {
        ...controls.overlays,
        [key]: !controls.overlays[key],
      },
    });
  }, [controls.overlays, onChange]);
  
  const toggleAdvanced = useCallback(() => {
    onChange({ showAdvanced: !controls.showAdvanced });
  }, [controls.showAdvanced, onChange]);
  
  
  const activeOverlayCount = Object.values(controls.overlays).filter(Boolean).length;
  
  return (
    <div className={cn("space-y-2", className)}>
      {/* Primary Controls Row */}
      <div className="flex flex-wrap items-center gap-3 p-2 bg-muted/30 rounded-lg border">
        {/* Face View */}
        <div className="flex items-center gap-2">
          <Label className="text-xs text-muted-foreground">Face:</Label>
          <ToggleGroup
            type="single"
            value={controls.faceView}
            onValueChange={handleFaceViewChange}
            className="gap-0.5"
          >
            <ToggleGroupItem value="front" size="sm" className="h-7 px-2 text-xs">
              Front
            </ToggleGroupItem>
            <ToggleGroupItem value="back" size="sm" className="h-7 px-2 text-xs">
              Back
            </ToggleGroupItem>
            <ToggleGroupItem value="both" size="sm" className="h-7 px-2 text-xs gap-1">
              <Layers className="h-3 w-3" />
              Both
            </ToggleGroupItem>
          </ToggleGroup>
        </div>
        
        <Separator orientation="vertical" className="h-6" />
        
        {/* Breakpoint */}
        <div className="flex items-center gap-2">
          <Label className="text-xs text-muted-foreground">Size:</Label>
          <ToggleGroup
            type="single"
            value={controls.viewportWidth.toString()}
            onValueChange={handleBreakpointChange}
            className="gap-0.5"
          >
            {BREAKPOINT_PRESETS.map(({ value, label, icon: Icon }) => (
              <ToggleGroupItem
                key={value}
                value={value.toString()}
                size="sm"
                className="h-7 px-2 text-xs gap-1"
              >
                <Icon className="h-3 w-3" />
                {label}
              </ToggleGroupItem>
            ))}
          </ToggleGroup>
        </div>
        
        <Separator orientation="vertical" className="h-6" />
        
        {/* Display Mode */}
        <div className="flex items-center gap-2">
          <Label className="text-xs text-muted-foreground">View:</Label>
          <ToggleGroup
            type="single"
            value={controls.displayMode}
            onValueChange={handleDisplayModeChange}
            className="gap-0.5"
          >
            <ToggleGroupItem value="fit" size="sm" className="h-7 px-2 text-xs gap-1">
              <Minimize2 className="h-3 w-3" />
              Fit
            </ToggleGroupItem>
            <ToggleGroupItem value="full-card" size="sm" className="h-7 px-2 text-xs gap-1">
              <Square className="h-3 w-3" />
              Full
            </ToggleGroupItem>
            <ToggleGroupItem value="fullscreen" size="sm" className="h-7 px-2 text-xs gap-1">
              <Maximize2 className="h-3 w-3" />
              Max
            </ToggleGroupItem>
          </ToggleGroup>
        </div>
        
        <Separator orientation="vertical" className="h-6" />
        
        {/* CR308: Height Mode */}
        <div className="flex items-center gap-2">
          <Label className="text-xs text-muted-foreground">Height:</Label>
          <ToggleGroup
            type="single"
            value={controls.heightMode}
            onValueChange={handleHeightModeChange}
            className="gap-0.5"
          >
            <ToggleGroupItem value="auto" size="sm" className="h-7 px-2 text-xs gap-1">
              <ArrowUpDown className="h-3 w-3" />
              Auto
            </ToggleGroupItem>
            <ToggleGroupItem value="scroll" size="sm" className="h-7 px-2 text-xs">
              Scroll
            </ToggleGroupItem>
            <ToggleGroupItem value="fixed" size="sm" className="h-7 px-2 text-xs">
              Fixed
            </ToggleGroupItem>
          </ToggleGroup>
        </div>
        
        
        <div className="flex-1" />
        
        {/* Advanced Toggle */}
        <Button
          variant={controls.showAdvanced ? "secondary" : "ghost"}
          size="sm"
          className="h-7 px-2 gap-1"
          onClick={toggleAdvanced}
        >
          <Settings2 className="h-3.5 w-3.5" />
          <span className="text-xs">Advanced</span>
          <ChevronDown className={cn(
            "h-3 w-3 transition-transform",
            controls.showAdvanced && "rotate-180"
          )} />
        </Button>
      </div>
      
      {/* Advanced Controls (Collapsible) */}
      <Collapsible open={controls.showAdvanced}>
        <CollapsibleContent>
          <div className="flex flex-wrap items-center gap-3 p-2 bg-muted/20 rounded-lg border border-dashed">
            {/* Custom Width/Height */}
            <div className="flex items-center gap-2">
              <Label className="text-xs text-muted-foreground">W:</Label>
              <Input
                type="number"
                value={controls.viewportWidth === "custom" ? controls.customWidth : 
                  (typeof controls.viewportWidth === "number" ? controls.viewportWidth : controls.customWidth)}
                onChange={handleCustomWidthChange}
                min={200}
                max={1000}
                className="h-7 w-16 text-xs"
              />
              <Label className="text-xs text-muted-foreground">H:</Label>
              <Input
                type="number"
                value={controls.customHeight}
                onChange={handleCustomHeightChange}
                min={200}
                max={1200}
                className="h-7 w-16 text-xs"
              />
            </div>
            
            <Separator orientation="vertical" className="h-6" />
            
            {/* Scale Mode */}
            <div className="flex items-center gap-2">
              <Label className="text-xs text-muted-foreground">Scale:</Label>
              <ToggleGroup
                type="single"
                value={controls.scaleMode}
                onValueChange={handleScaleModeChange}
                className="gap-0.5"
              >
                <ToggleGroupItem value="fit" size="sm" className="h-7 px-2 text-xs">
                  Fit
                </ToggleGroupItem>
                <ToggleGroupItem value="actual" size="sm" className="h-7 px-2 text-xs">
                  1:1
                </ToggleGroupItem>
                <ToggleGroupItem value="custom" size="sm" className="h-7 px-2 text-xs">
                  Zoom
                </ToggleGroupItem>
              </ToggleGroup>
              
              {controls.scaleMode === "custom" && (
                <div className="flex items-center gap-1">
                  <ZoomOut className="h-3 w-3 text-muted-foreground" />
                  <Slider
                    value={[controls.customScale]}
                    onValueChange={handleCustomScaleChange}
                    min={50}
                    max={150}
                    step={5}
                    className="w-16"
                  />
                  <ZoomIn className="h-3 w-3 text-muted-foreground" />
                  <span className="text-[10px] text-muted-foreground w-6">{controls.customScale}%</span>
                </div>
              )}
            </div>
            
            <Separator orientation="vertical" className="h-6" />
            
            {/* Debug Overlays */}
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="ghost" size="sm" className="h-7 px-2 gap-1 relative">
                  <Grid3X3 className="h-3.5 w-3.5" />
                  <span className="text-xs">Overlays</span>
                  {activeOverlayCount > 0 && (
                    <span className="absolute -top-1 -right-1 h-4 w-4 text-[9px] bg-primary text-primary-foreground rounded-full flex items-center justify-center">
                      {activeOverlayCount}
                    </span>
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-48 p-2" align="end">
                <div className="space-y-2">
                  {[
                    { key: "showFoldLine" as const, label: "Fold line", icon: Ruler },
                    { key: "showSectionBounds" as const, label: "Section bounds", icon: Box },
                    { key: "showHiddenBlocks" as const, label: "Hidden blocks", icon: EyeOff },
                    { key: "showSafePadding" as const, label: "Safe padding", icon: Grid3X3 },
                  ].map(({ key, label, icon: Icon }) => (
                    <div key={key} className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-xs">
                        <Icon className="h-3 w-3" />
                        {label}
                      </div>
                      <Switch
                        checked={controls.overlays[key]}
                        onCheckedChange={() => handleOverlayToggle(key)}
                      />
                    </div>
                  ))}
                </div>
              </PopoverContent>
            </Popover>
            
            {/* Reset */}
            {onReset && (
              <Button variant="ghost" size="sm" onClick={onReset} className="h-7 px-2 gap-1">
                <RotateCcw className="h-3 w-3" />
                <span className="text-xs">Reset</span>
              </Button>
            )}
          </div>
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
}